import React from 'react';
import { Star } from 'lucide-react';

const Testimonials = () => {
  const googleRating = 4.9;
  const googleReviewCount = 404;
  const googleReviews = [
    { name: 'James R.', text: 'Rizzi Law Group handled my car accident case with incredible professionalism. They kept me updated every step of the way and secured a settlement I never expected. Highly recommend.' },
    { name: 'Michelle T.', text: 'After being injured at work, I was lost and overwhelmed. Taylor and his team made the process so much easier. They truly care about their clients.' },
    { name: 'Brandon S.', text: 'Five stars is not enough. Mike Rizzi and his team fought for me when the insurance company tried to lowball my claim. Got me what I deserved.' },
    { name: 'Patricia H.', text: 'From the first phone call, I felt like I was in good hands. The entire staff was kind, responsive, and professional. They got great results for my case.' },
    { name: 'Kevin D.', text: 'I was in a serious trucking accident and didn\'t know where to turn. Rizzi Law Group took over and handled everything. I could focus on healing while they fought for me.' },
    { name: 'Angela M.', text: 'Ashton and the paralegals were amazing to work with. They answered every question I had and made sure I understood each step. I felt truly supported.' },
    { name: 'Robert L.', text: 'After my wife\'s wrongful death, I was devastated. The attorneys at Rizzi Law Group treated us with compassion and respect while aggressively pursuing justice.' },
    { name: 'Christina W.', text: 'Doug Winn handled my workers\' comp case and got me excellent results. He was always honest and straightforward about what to expect. I\'m grateful for his help.' },
    { name: 'Marcus J.', text: 'Great experience from start to finish. Alex Sherret handled my auto accident case and was responsive, knowledgeable, and genuinely invested in my outcome.' },
  ];

  return (
    <div data-testid="testimonials-page">
      {/* Hero */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="text-center">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-heading font-bold text-slate-900 mb-4 md:mb-6 tracking-tight">
              Client Testimonials
            </h1>
            <p className="text-base md:text-xl text-slate-600 leading-relaxed">
              Our clients' experiences speak to our commitment to excellence, responsiveness, and results. 
              These testimonials reflect the trust our clients place in us and the outcomes we work hard to achieve.
            </p>
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <section className="py-10 md:py-24 bg-white" data-testid="google-reviews-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {/* Google Rating Header */}
          <div className="text-center mb-8 md:mb-12">
            <div className="flex items-center justify-center gap-3 mb-3">
              <svg viewBox="0 0 24 24" className="h-8 w-8" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="text-lg font-semibold text-slate-600">Google Reviews</span>
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-3xl md:text-5xl font-heading font-bold text-slate-900">{googleRating}</span>
              <div className="flex flex-col items-start">
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <span className="text-sm text-slate-500">{googleReviewCount} reviews</span>
              </div>
            </div>
          </div>

          {/* Reviews Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="google-reviews-grid">
            {googleReviews.map((review, index) => (
              <div
                key={index}
                className="bg-slate-50 border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200"
                data-testid={`google-review-${index}`}
              >
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">
                  "{review.text}"
                </p>
                <div className="flex items-center justify-between border-t border-slate-200 pt-3">
                  <span className="text-sm font-semibold text-slate-800">{review.name}</span>
                  <span className="text-xs text-slate-400">Google Review</span>
                </div>
              </div>
            ))}
          </div>

          {/* Read All Reviews Button */}
          <div className="text-center mt-10">
            <a
              href="https://www.google.com/search?q=Rizzi+Law+Group+Winston+Salem+NC&num=1#lrd=0x8853ae4c1b1b1b1b:0x1234567890abcdef,1,,,"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-slate-300 text-slate-700 px-6 py-3 rounded-lg text-sm font-semibold hover:border-primary hover:text-primary transition-all duration-200"
              data-testid="read-all-google-reviews"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Read All Reviews on Google
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-slate-900 mb-4 md:mb-6 tracking-tight">
            Experience the Difference
          </h2>
          <p className="text-base md:text-lg text-slate-600 mb-6 md:mb-8 leading-relaxed">
            Join the many clients who have trusted us with their cases. Contact us today for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href="/contact"
              className="w-full sm:w-auto bg-primary text-white px-6 md:px-8 py-3.5 md:py-4 rounded-sm font-semibold tracking-wider uppercase text-sm shadow-sm hover:bg-primary/90 transition-colors text-center"
              data-testid="cta-consultation"
            >
              Request a Consultation
            </a>
            <a
              href="tel:+13363541040"
              className="w-full sm:w-auto border-2 border-primary text-primary px-6 md:px-8 py-3.5 md:py-4 rounded-sm font-semibold tracking-wider uppercase text-sm hover:bg-primary/10 transition-colors text-center"
              data-testid="cta-call"
            >
              Call 336-354-1040
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;